import React, { type FC, useContext, useState } from 'react';

import { compose } from 'redux';

import { reloadCompany } from 'common/actions/company';
import AJAX from 'common/AJAX';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import connect from 'common/core/connect';
import Helmet from 'common/helmets/Helmet';
import useMutation from 'common/hooks/useMutation';
import withAccessControl from 'common/routing/withAccessControl';
import Alert, { AlertTypes } from 'common/ui/Alert';
import Button from 'common/uiV3/Button';
import ButtonLink from 'common/uiV3/Button/ButtonLink';
import HStack from 'common/uiV3/HStack';
import { Separator } from 'common/uiV3/Separator';
import Switch from 'common/uiV3/Switch';
import VStack from 'common/uiV3/VStack';
import { RoutePermissions, testEveryPermission } from 'common/util/permissions';

import type { Company } from 'common/api/endpoints/companies';

type OwnProps = Record<string, never>;

type ConnectProps = {
  reloadCompany: () => void;
};

type Props = OwnProps & ConnectProps;

const AdminIdentifySettings: FC<Props> = ({ reloadCompany }: Props) => {
  const company = useContext<Company>(CompanyContext);
  const showToast = useContext(ShowToastContext);

  const [secureIdentify, setSecureIdentify] = useState<boolean>(company.secureIdentify);
  const [defaultIdentifyMode, setDefaultIdentifyMode] = useState<'upsert' | 'update'>(
    company.defaultIdentifyMode,
  );

  const savePreferences = useMutation({
    mutationFn: async (data) => AJAX.post('/api/company/changeSettings', data),
    onSuccess: async () => {
      await reloadCompany();
    },
    onError: (error: Error) => {
      showToast(error.message, ToastTypes.error);
    },
  });

  const hasChanges =
    secureIdentify !== company.secureIdentify ||
    defaultIdentifyMode !== company.defaultIdentifyMode;

  return (
    <div className="max-w-[720px]">
      <Helmet title="Identify | Authentication | Canny" />
      <VStack className="gap-6">
        <VStack className="gap-2">
          <h1 className="text-xl font-bold">Identify</h1>
          <p>
            Canny Identify is a component of the Canny SDK that allows you to automatically
            authenticate your users in Canny, and enrich their profiles with business data.
          </p>
        </VStack>

        <Separator />

        <VStack className="gap-2">
          <h2 className="text-lg">Identify security</h2>
          <p>
            Keep your organization's accounts safe when identifying users through the Canny SDK.
            Securing identify requests helps prevent impersonation and unauthorized access to
            sensitive data, especially when you use{' '}
            <ButtonLink
              variant="link"
              rel="nofollow noopener"
              href="https://help.canny.io/en/articles/9829559-custom-access-boards">
              custom access boards
            </ButtonLink>
            .
          </p>
          <p>
            Require a per-user hash with each identify call to make sure requests to Canny are
            coming from your backend and haven't been tampered with. This prevents users from
            pretending to be someone else in Canny.
          </p>
          <p>
            <ButtonLink
              variant="link"
              href="https://help.canny.io/en/articles/10036470-secure-identify-requests-to-canny-sdk"
              rel="nofollow noopener">
              Learn more about why securing identify matters
            </ButtonLink>
            , and{' '}
            <ButtonLink
              variant="link"
              href="https://developers.canny.io/install/security"
              rel="nofollow noopener">
              learn how to secure Identify
            </ButtonLink>
            .
          </p>

          <HStack className="gap-4 mt-2 items-center">
            <p>Require a user-specific hash when identifying users through the Canny SDK.</p>
            <Switch
              checked={secureIdentify}
              onCheckedChange={(checked) => setSecureIdentify(checked)}
            />
          </HStack>

          {secureIdentify !== company.secureIdentify && secureIdentify && (
            <Alert type={AlertTypes.Warning}>
              <Alert.Heading>Warning</Alert.Heading>
              <Alert.Content>
                To avoid errors, confirm that you've updated the Canny identify snippet in your
                application before enabling this feature.
              </Alert.Content>
            </Alert>
          )}
        </VStack>

        <Separator />

        <VStack className="gap-2">
          <h2 className="text-lg">Identify mode</h2>
          <p>Control how Canny handles identify calls for users that don't exist yet.</p>
          <p>
            When enabled, Identify will only update existing users. If your application sends an
            identify call for a user that doesn't exist in Canny, that call will not create a new
            user.
          </p>
          <p>
            When disabled, Identify will create new users if they don't already exist (upsert
            behavior). This is helpful when you want to search for your users when voting on behalf
            of them in Canny. Keep in mind that using upsert means Canny will store user data for
            more people who may never become active Canny users.
          </p>
          <p>
            Note users who access Canny via a link on your site,{' '}
            <strong>will still be created automatically</strong>, regardless of this setting.
          </p>
          <p>
            This mode can be overwritten by the mode parameter in the identify call. Read more about
            how Identify's default mode works{' '}
            <ButtonLink
              variant="link"
              href="https://developers.canny.io/install/advanced"
              rel="nofollow noopener">
              in our docs
            </ButtonLink>
            .
          </p>

          <HStack className="gap-4 mt-2 items-center">
            <p>By default, only update existing users.</p>
            <Switch
              checked={defaultIdentifyMode === 'update'}
              onCheckedChange={(checked) => setDefaultIdentifyMode(checked ? 'update' : 'upsert')}
            />
          </HStack>
        </VStack>

        <HStack>
          <Button
            color="primary"
            disabled={!hasChanges}
            isLoading={savePreferences.isLoading}
            onClick={() => savePreferences.mutate({ secureIdentify, defaultIdentifyMode })}>
            Save
          </Button>
        </HStack>
      </VStack>
    </div>
  );
};

export default compose(
  connect(null, { reloadCompany }),
  withAccessControl<Props>(
    testEveryPermission(RoutePermissions.adminSettings.security.identify),
    '/admin/settings',
  ),
)(AdminIdentifySettings) as unknown as React.FC<OwnProps>;
